import { ListBox } from '@repo/foundations';
import { useCallback, useMemo, useState } from 'react';
import { FilterMenuItem } from '../FilterMenuItem';
import {
	FILTER_OPTIONS_DIVIDER,
	IS_NOT_SET_FILTER_ITEM,
	IS_SET_FILTER_ITEM,
} from '../constants';
import type { FilterValue } from '../types';
import { FilterType } from '../types';
import { getValueAsArray } from '../utils';

export interface FilterDropdownStringProps {
	value: FilterValue;
	onChange: (value: FilterValue, close: boolean) => void;
	inputPlaceholder?: string;
	hasIsNotSetOption?: boolean;
	hasIsSetOption?: boolean;
	prefilledOptions?: { label: string; value: string }[];
}

export function FilterDropdownString({
	value,
	onChange,
	inputPlaceholder,
	hasIsNotSetOption,
	hasIsSetOption,
	prefilledOptions,
}: FilterDropdownStringProps) {
	const [stringValue, setStringValue] = useState<string>('');

	const selectedValues = useMemo(() => getValueAsArray(value), [value]);

	const onSelectIsNotSet = useCallback(
		(close: boolean) => {
			onChange(
				{
					...value,
					isNotSetApplied: true,
					isSetApplied: false,
					value: null,
				},
				close
			);
		},
		[onChange, value]
	);

	const onDeselectIsNotSet = useCallback(
		(close: boolean) => {
			onChange({ ...value, isNotSetApplied: false }, close);
		},
		[onChange, value]
	);

	const onSelectIsSet = useCallback(
		(close: boolean) => {
			onChange(
				{
					...value,
					isSetApplied: true,
					isNotSetApplied: false,
					value: null,
				},
				close
			);
		},
		[onChange, value]
	);

	const onDeselectIsSet = useCallback(
		(close: boolean) => {
			onChange({ ...value, isSetApplied: false }, close);
		},
		[onChange, value]
	);

	const onSelect = useCallback(
		(v: string) => (close: boolean) => {
			const current = getValueAsArray(value);
			const newValues = current.length >= 1 ? [...current, v] : [v];
			const newValue = newValues.length === 1 ? newValues[0]! : newValues;
			onChange(
				{
					...value,
					value: newValue,
					isSetApplied: false,
					isNotSetApplied: false,
				},
				close
			);
		},
		[onChange, value]
	);

	const onDeselectValue = useCallback(
		(v: string) => (close: boolean) => {
			const newValues = getValueAsArray(value).filter((x) => x !== v);
			const newValue =
				newValues.length === 0
					? null
					: newValues.length === 1
						? newValues[0]!
						: newValues;
			onChange(
				{
					...value,
					value: newValue,
				},
				close
			);
		},
		[onChange, value]
	);

	const items = useMemo(() => {
		const result: Array<
			| string
			| { value: string; label: string; prefilled: true }
			| typeof IS_SET_FILTER_ITEM
			| typeof IS_NOT_SET_FILTER_ITEM
		> = [];

		for (const v of selectedValues) {
			if (typeof v === 'string') {
				result.push(v);
			}
		}

		if (selectedValues.length > 0) {
			result.push(FILTER_OPTIONS_DIVIDER);
		}

		// Typed value as option to add
		if (stringValue && !selectedValues.includes(stringValue)) {
			result.push(stringValue);
		}

		if (hasIsSetOption) {
			result.push(IS_SET_FILTER_ITEM);
		}

		if (hasIsNotSetOption) {
			result.push(IS_NOT_SET_FILTER_ITEM);
		}

		if (prefilledOptions) {
			for (const option of prefilledOptions) {
				if (
					option.value !== stringValue &&
					option.value.includes(stringValue) &&
					!selectedValues.includes(option.value)
				) {
					result.push({
						value: option.value,
						label: option.label,
						prefilled: true,
					});
				}
			}
		}

		return result;
	}, [
		hasIsNotSetOption,
		hasIsSetOption,
		prefilledOptions,
		stringValue,
		selectedValues,
	]);

	const getInitialActiveIndex = useCallback(
		(
			itemsList: Array<
				| string
				| { value: string; label: string; prefilled: true }
				| typeof IS_SET_FILTER_ITEM
				| typeof IS_NOT_SET_FILTER_ITEM
				| typeof FILTER_OPTIONS_DIVIDER
			>
		) => {
			if (!stringValue || !itemsList.length) {
				return 0;
			}
			const matchIndex = itemsList.findIndex((item) => {
				if (item === FILTER_OPTIONS_DIVIDER) return false;
				if (typeof item === 'string') return item === stringValue;
				if (
					typeof item === 'object' &&
					item !== null &&
					'value' in item &&
					item.value === stringValue
				) {
					return true;
				}
				return false;
			});
			return matchIndex >= 0 ? matchIndex : 0;
		},
		[stringValue]
	);

	return (
		<ListBox.ItemsDropdown
			search={{
				onChange: setStringValue,
				value: stringValue,
				placeholder: inputPlaceholder ?? 'Type',
			}}
			items={items}
			getInitialActiveIndex={getInitialActiveIndex}
			renderItem={(item, getProps) => {
				if (item === FILTER_OPTIONS_DIVIDER) {
					return <ListBox.Divider key="divider" />;
				}
				if (item === IS_SET_FILTER_ITEM) {
					return (
						<FilterMenuItem
							key="IS_SET"
							item={item}
							filterType={FilterType.Multiple}
							selected={value.isSetApplied}
							onClick={value.isSetApplied ? onDeselectIsSet : onSelectIsSet}
							getProps={getProps}
						/>
					);
				}

				if (item === IS_NOT_SET_FILTER_ITEM) {
					return (
						<FilterMenuItem
							key="IS_NOT_SET"
							item={item}
							filterType={FilterType.Multiple}
							selected={value.isNotSetApplied}
							onClick={
								value.isNotSetApplied ? onDeselectIsNotSet : onSelectIsNotSet
							}
							getProps={getProps}
						/>
					);
				}

				if (typeof item === 'object' && 'prefilled' in item) {
					const isSelected = selectedValues.includes(item.value);
					return (
						<FilterMenuItem
							key={`filter-value-${item.value}-prefill`}
							item={{ label: item.label, value: item.value }}
							filterType={FilterType.Multiple}
							selected={isSelected}
							onClick={
								isSelected
									? onDeselectValue(item.value)
									: onSelect(item.value as string)
							}
							getProps={getProps}
						/>
					);
				}

				const stringItem = item as string;
				const isSelected = selectedValues.includes(stringItem);
				return (
					<FilterMenuItem
						key={`filter-value-${stringItem}`}
						item={{ label: stringItem, value: stringItem }}
						filterType={FilterType.Multiple}
						selected={isSelected}
						onClick={
							isSelected ? onDeselectValue(stringItem) : onSelect(stringItem)
						}
						getProps={getProps}
					/>
				);
			}}
		/>
	);
}
